import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import css from './DataSourcePicker.module.scss';

// 数据源切换 - 多选合并版
// - checkbox 多选，至少保留 1 个
// - 快捷键："全选 LPL"、"全选 LCK"、"只选当前一项"
// - 切换后整张图谱、AI 推荐、徽章会自动重算
const DataSourcePicker = ({
  sources,
  selectedSourceIds,
  onToggle,
  onSelectOnly,
  onSelectByLeague,
  loading,
  currentMeta,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 推算可用联赛集合
  const leagues = useMemo(() => {
    const s = new Set();
    sources.forEach((src) => { if (src.league) s.add(src.league); });
    return Array.from(s);
  }, [sources]);

  if (!sources || sources.length === 0) return null;

  const count = selectedSourceIds.length;
  const totalGames = currentMeta ? currentMeta.totalGames : null;
  const headline = currentMeta && count === 1
    ? currentMeta.label
    : `${count} 源合并`;
  const fullLabel = currentMeta ? currentMeta.label : '加载中…';

  return (
    <div className={css.Root} ref={rootRef} data-export-ignore="true">
      <button
        type="button"
        className={cx(css.Trigger, open && css.TriggerOpen, loading && css.TriggerLoading)}
        onClick={() => setOpen((o) => !o)}
        title={`当前：${fullLabel}` + (totalGames ? ` · ${totalGames} 场` : '')}
      >
        <span className={css.TriggerIcon} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <ellipse cx="12" cy="5"  rx="9" ry="3" />
            <path    d="M3 5v6c0 1.66 4 3 9 3s9-1.34 9-3V5" />
            <path    d="M3 11v6c0 1.66 4 3 9 3s9-1.34 9-3v-6" />
          </svg>
        </span>
        <span className={css.TriggerLabel}>
          <span className={css.TriggerHead}>
            数据源 {count > 1 && <span className={css.TriggerBadge}>×{count}</span>}
          </span>
          <span className={css.TriggerBody}>
            {headline}
            {totalGames ? <span className={css.TriggerGames}>·{totalGames}场</span> : null}
          </span>
        </span>
        <span className={cx(css.Caret, open && css.CaretOpen)} aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className={css.Menu} role="listbox" aria-multiselectable="true">
          <div className={css.MenuHead}>
            <span>多选合并 · 已选 {count}</span>
            <div className={css.QuickActions}>
              {leagues.map((lg) => (
                <button
                  key={lg}
                  type="button"
                  className={css.QuickBtn}
                  onClick={() => onSelectByLeague(lg)}
                  title={`只选 ${lg} 所有源`}
                >
                  全选 {lg}
                </button>
              ))}
            </div>
          </div>

          {sources.map((s) => {
            const checked = selectedSourceIds.includes(s.id);
            const isOnly = checked && selectedSourceIds.length === 1;
            return (
              <div
                key={s.id}
                className={cx(css.Item, checked && css.ItemChecked)}
                role="option"
                aria-selected={checked}
              >
                <button
                  type="button"
                  className={css.ItemRow}
                  onClick={() => onToggle(s.id)}
                  // 单选状态下再点取消 → 直接禁用避免取消到 0 个
                  disabled={isOnly}
                  title={isOnly ? '至少保留 1 个数据源' : (checked ? '取消选中' : '加入合并')}
                >
                  <span className={cx(css.Checkbox, checked && css.CheckboxOn)}>
                    {checked && <span className={css.CheckMark}>✓</span>}
                  </span>
                  <span className={cx(css.ItemTag, css[`Tag_${s.tag || 'regular'}`])}>
                    {tagLabel(s.tag)}
                  </span>
                  <span className={css.ItemBody}>
                    <span className={css.ItemLabel}>{s.label}</span>
                    <span className={css.ItemMeta}>
                      {s.league} · {s.season}{s.split ? ` · ${s.split}` : ''}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className={css.OnlyBtn}
                  onClick={() => onSelectOnly(s.id)}
                  title={`只看 ${s.shortLabel || s.label} 一份`}
                >
                  仅此
                </button>
              </div>
            );
          })}

          <div className={css.MenuFoot}>
            勾选多个会自动累加 picks/bans 并重算 PR/BR/WR；选择会记在本地，下次刷新还在。
          </div>
        </div>
      )}
    </div>
  );
};

const tagLabel = (tag) => {
  switch (tag) {
    case 'playoff': return '季后';
    case 'regular': return '常规';
    case 'archive': return '历史';
    case 'worlds':  return '世界';
    case 'msi':     return 'MSI';
    default:        return '赛事';
  }
};

export default DataSourcePicker;
